import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FileUploadType } from '../../../../enums';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { TranslocoService } from '@jsverse/transloco';
import { take, takeUntil } from 'rxjs/operators';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  BatchPostingService,
  commonRegex,
  Organization,
  OrganizationState,
  UpdateVehicleUploadState,
  UploadInServiceVehiclesState,
  UploadStatus,
  UserProfile,
  UserProfileState
} from 'remarketing-angular-library';
import {
  CommonBannerComponent,
  ModalComponent,
  SpinnerComponent
} from 'remarketing-ui-components-lib';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-update-in-service-vehicles-shell',
    templateUrl: './upload-in-service-vehicles-shell.component.html',
    styleUrls: ['./upload-in-service-vehicles-shell.component.scss'],
    imports: [ErrorModalComponent, CommonBannerComponent, FormsModule, SpinnerComponent, CommonModule]
})
export class UploadInServiceVehiclesShellComponent extends SaComponent implements OnInit {
  @Select(UploadInServiceVehiclesState.uploadStep) uploadStep$: Observable<UploadStatus>;
  @ViewChild('fileUpload') fileupload: ElementRef;
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  organization: Organization;
  userProfile: UserProfile;
  UploadStatus = UploadStatus; // Assign enum to class level variable to use in html.
  isFileTypeSelected: string;
  file: File;
  fileName: string;
  fileType: string;
  uploadStep: UploadStatus;
  uploadProcessing = false;
  fileUploaded = false;
  isOnDemandFile = false;
  isBatchFile = false;
  uploadingFile = false;

  fileSizeError$ = new BehaviorSubject<boolean>(false);
  fileNameError$ = new BehaviorSubject<boolean>(false);
  fileTypeError$ = new BehaviorSubject<boolean>(false);
  fileGeneralError$ = new BehaviorSubject<boolean>(false);

  fileSizeErrorValue = this.fileSizeError$.asObservable();
  fileNameError = this.fileNameError$.asObservable();
  fileTypeError = this.fileTypeError$.asObservable();
  fileGeneralError = this.fileGeneralError$.asObservable();

  uploadInServiceVehicleShellTranslations = {
    header: '',
    selectText: '',
    acceptText: '',
    fileNameText: '',
    selectFileText: '',
    fileType: '',
    batchText: '',
    onDemandText: '',
    uploadText: '',
    statusSuccessTextTop: '',
    statusSuccessButtonText: '',
    statusSuccessNoteText: '',
    batchFileBeingProcessedAlert: '',
    batchFileBeingProcessed: '',
    onDemandFileBeingProcessed: '',
    bothFilesBeingProcessed: '',
    cantCompleteRequestError: '',
    duplicateVinError: ''
  };

  banner = {
    text: '',
    mode: 'none',
    showIcon: true,
    showUndo: false,
    showErrorIcon: true,
    showSuccessIcon: false
  };

  errorMessage: string;
  duplicateVins: string[];

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly store: Store,
    private readonly batchPostingService: BatchPostingService,
    private readonly analyticsService: AnalyticsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getOrgAndUserProfile();
    setTimeout(() => {
      this.analyticsAtPageload(false);
    }, 500);
    this.getTranslations();
    this.checkFileStatus();
    this.uploadStep$.pipe(takeUntil(this.destroyed$)).subscribe(status => {
      this.uploadStep = status;
    });
  }

  checkFileStatus() {
    this.batchPostingService
      .getBatchPostingFiles()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const { batch, onDemand } = response;
          this.isBatchFile = batch;
          this.isOnDemandFile = onDemand;
          if (this.isBatchFile && this.isOnDemandFile) {
            // If both batch file and on demand file is being processed.
            this.banner.text = this.uploadInServiceVehicleShellTranslations.bothFilesBeingProcessed;
            this.uploadProcessing = true;
          } else if (this.isBatchFile && !this.isOnDemandFile) {
            // If batch file is being processed.
            this.banner.text = this.uploadInServiceVehicleShellTranslations.batchFileBeingProcessed;
            this.uploadProcessing = true;
          } else if (!this.isBatchFile && this.isOnDemandFile) {
            // If on demand file is being processed.
            this.banner.text = this.uploadInServiceVehicleShellTranslations.onDemandFileBeingProcessed;
            this.uploadProcessing = true;
          } else {
            // If no file being processed, hide the banner.
            this.uploadProcessing = false;
          }
        },
        error: error => {
          this.uploadProcessing = false;
          console.error(error);
        }
      });
  }

  onFileSelected(uploadedFile): void {
    const allowedFileTypes = ['csv'];
    const file = uploadedFile.srcElement.files[0];
    const ext = file?.name.includes('.') ? file.name.match(/(.+)+\.(.+)/).slice(1) : null;

    // Prevent console error if user cancels upload without uploading anything.
    if (file && ext) {
      // Only allow the file upload if the file extension is inside the allowedFileTypes array.
      allowedFileTypes.forEach(fileType => {
        if (fileType === ext[1]) {
          this.file = file;
          this.fileName = file?.name;
        }
      });
    } else {
      console.error('Failed to upload file.');
      this.fileupload.nativeElement.value = '';
    }
  }

  fileTypeSelection(event: Event): FileUploadType {
    return (this.fileType =
      FileUploadType.BATCH_FILE === (event.target as HTMLInputElement).value
        ? FileUploadType.BATCH_FILE
        : FileUploadType.ON_DEMAND_FILE);
  }

  isFileNameAlphaNumeric(fileName): boolean {
    return commonRegex.alphaNumericOnly.test(fileName);
  }

  uploadFile(file) {
    this.uploadingFile = true;
    this.resetFormErrors(); // Reset errors
    commonRegex.alphaNumericOnly.lastIndex = 0; // Reset regex to prevent toggling of value.
    this.analyticsUploadFileOnClick();
    let filename = '';
    const fullOrPartialFile =
      this.fileType !== FileUploadType.BATCH_FILE ? FileUploadType.PARTIAL : FileUploadType.FULL;
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('batchPostingType', fullOrPartialFile);

    file ? ([filename] = file.name.match(/(.+)+\.(.+)/).slice(1)) : null;

    if (!this.isFileTypeSelected) {
      // If no type selection is made.
      this.fileTypeError$.next(true);
      this.uploadingFile = false;
    } else if (!file) {
      // If no file uploaded.
      this.fileGeneralError$.next(true);
      this.uploadingFile = false;
    } else if (this.file?.size > 10000000) {
      // If file bigger than 10MB.
      this.fileSizeError$.next(true);
      this.uploadingFile = false;
    } else if (this.isFileNameAlphaNumeric(filename)) {
      this.fileNameError$.next(true);
      this.uploadingFile = false;
    } else {
      // Success
      this.batchPostingService.postBatchInServiceVehicleFile(formData).subscribe({
        next: () => {
          this.store.dispatch(new UpdateVehicleUploadState(UploadStatus.SUCCESS_UPLOADED));
          this.uploadingFile = false;
          this.analyticsAtPageload(true);
        },
        error: error => {
          console.error(error);
          if (error.error.errors) {
            this.setErrorMessage(error);
            this.errorModal.message = this.errorMessage;
            this.errorModal.show();
          } else {
            // when service is down or timing out waiting for response, to show generic error.
            this.errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
            this.errorModal.message = this.errorMessage;
            this.errorModal.show();
          }
          this.analyticsService.track('customError', 'File upload error');
          this.uploadingFile = false;
        }
      });
    }
  }

  resetFormErrors() {
    this.fileSizeError$.next(false);
    this.fileNameError$.next(false);
    this.fileTypeError$.next(false);
    this.fileGeneralError$.next(false);
  }

  backToUploadStatus() {
    this.store.dispatch(new UpdateVehicleUploadState(UploadStatus.UPLOAD));
    setTimeout(() => {
      this.analyticsAtPageload(false);
    }, 100);
    this.fileUploaded = true;
    this.removeFile();
  }

  removeFile() {
    this.file = null;
    this.fileupload ? (this.fileupload.nativeElement.value = '') : null; // This will remove file from input element.
    this.resetFormErrors();
  }

  fileUploadEvent() {
    this.fileupload.nativeElement.click();
    this.analyticsService.track('customEvent', {
      pageName: `${this.analyticsService.digitalData.page.category.site}:Sell:Upload In-Service Vehicles`,
      name: 'Add File',
      type: 'Custom Event',
      category: 'UserInteraction:CustomEvent'
    });
  }

  getOrgAndUserProfile(): void {
    this.organization = this.store.selectSnapshot(OrganizationState.organizationState).organization;
    this.userProfile = this.store.selectSnapshot(UserProfileState.userProfileState).userProfile;
  }

  analyticsUploadFileOnClick() {
    this.analyticsService.track('customEvent', {
      name: 'uploadInServiceVehicles',
      type: 'Custom Event',
      category: 'UserInteraction:CustomEvent',
      attr: {
        radioSelected: this.fileType === FileUploadType.BATCH_FILE ? 'Batch' : 'On-demand'
      }
    });
  }

  analyticsAtPageload(isCompleteString: boolean) {
    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Sell:Upload In-Service Vehicles ${
          isCompleteString ? 'Complete' : ''
        }`,
        section: 'Sell',
        site: this.analyticsService.digitalData.page.category.site
      },
      user: {
        dealershipId: this.organization.oeUuid,
        dealershipName: this.organization.oeName,
        userId: this.userProfile.userId
      }
    });
  }

  getTranslations(): void {
    const keyParams = {
      'uploadInServiceVehicles.uploadInServiceVehiclesShell': {},
      error: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.uploadInServiceVehicleShellTranslations.header = translate[0].header;
        this.uploadInServiceVehicleShellTranslations.selectText = translate[0].selectText;
        this.uploadInServiceVehicleShellTranslations.acceptText = translate[0].acceptText;
        this.uploadInServiceVehicleShellTranslations.fileNameText = translate[0].fileNameText;
        this.uploadInServiceVehicleShellTranslations.selectFileText = translate[0].selectFileText;
        this.uploadInServiceVehicleShellTranslations.fileType = translate[0].fileType;
        this.uploadInServiceVehicleShellTranslations.batchText = translate[0].batchText;
        this.uploadInServiceVehicleShellTranslations.onDemandText = translate[0].onDemandText;
        this.uploadInServiceVehicleShellTranslations.uploadText = translate[0].uploadText;
        this.uploadInServiceVehicleShellTranslations.statusSuccessTextTop = translate[0].statusSuccessTextTop;
        this.uploadInServiceVehicleShellTranslations.statusSuccessButtonText = translate[0].statusSuccessButtonText;
        this.uploadInServiceVehicleShellTranslations.statusSuccessNoteText = translate[0].statusSuccessNoteText;
        this.uploadInServiceVehicleShellTranslations.batchFileBeingProcessedAlert =
          translate[0].batchFileBeingProcessedAlert;
        this.uploadInServiceVehicleShellTranslations.onDemandFileBeingProcessed =
          translate[0].onDemandFileBeingProcessed;
        this.uploadInServiceVehicleShellTranslations.batchFileBeingProcessed = translate[0].batchFileBeingProcessed;
        this.uploadInServiceVehicleShellTranslations.bothFilesBeingProcessed = translate[0].bothFilesBeingProcessed;
        this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError = translate[1].cantCompleteRequestError;
        this.uploadInServiceVehicleShellTranslations.duplicateVinError = translate[1].duplicateVinError;
      });
  }

  setErrorMessage(error) {
    let isFirstError = true;
    let errorMessage = '';
    const errors = error.error.errors;
    this.errorMessage = '';
    this.duplicateVins = [];
    if (errors) {
      errors.forEach(error => {
        errorMessage = this.mapErrorCodeToMessage(error, errorMessage);
        if (errorMessage.trim()) {
          if (!isFirstError) {
            // Delimit by new line if more than one error is thrown
            this.errorMessage += '\n';
          }
          this.errorMessage += errorMessage;
          isFirstError = false;
        }
      });
      this.formatDuplicateVinErrorMessage();
      // since innerHtml is used in error modal to render, need to replace new line with br to render accordingly
      this.errorMessage = this.errorMessage.replace(/\n/g, '<br>');
    } else {
      this.errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
    }
  }

  private formatDuplicateVinErrorMessage() {
    if (this.duplicateVins.length > 0) {
      if (this.errorMessage && this.errorMessage.length > 0) {
        this.errorMessage += '\n';
      }
      this.errorMessage +=
        this.uploadInServiceVehicleShellTranslations.duplicateVinError + `${this.duplicateVins.join(', ')}`;
    }
  }

  private mapErrorCodeToMessage(error, errorMessage: string) {
    const errorCode = error.code;
    switch (errorCode) {
      case 'F-REMPLT-BPS-DVIF':
        const vinMatch = error.detail.match(/file contains duplicate:\s*([A-Za-z0-9]+)/);
        if (vinMatch && vinMatch[1]) {
          this.duplicateVins.push(vinMatch[1]);
        }
        break;
      case 'F-REMPLT-BPS-IFC':
      case 'F-REMPLT-BPS-FHI':
      case 'F-REMPLT-BPS-FREL':
        // Replace file with File if first word of error message is starting that way.
        errorMessage = error.detail.toLowerCase().startsWith('file ')
          ? error.detail.replace(/^file /i, 'File ') + '.'
          : error.detail + '.';
        break;
      case 'F-REMPLT-BPS-UPLPGS':
        errorMessage = error.detail;
        break;
      default:
        errorMessage = this.uploadInServiceVehicleShellTranslations.cantCompleteRequestError;
        break;
    }
    return errorMessage;
  }
}
